import type {
  CreateReleaseResponse,
  ListReleasesQuery,
  ListReleasesResponse,
  ReleaseInput,
  ReleaseRecord,
  ResendLinkResponse,
  SendEmailResponse,
} from '@shared/types';
import { getInitData } from './telegram';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const headers = new Headers(opts.headers);
  headers.set('X-Telegram-Init-Data', getInitData());
  if (opts.body) headers.set('Content-Type', 'application/json');
  const res = await fetch(path, { ...opts, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || res.statusText);
  }
  return res.json() as Promise<T>;
}

const qs = (query: ListReleasesQuery): string => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
};

export { ApiError };

export const adminApi = {
  createRelease: (input: ReleaseInput) =>
    request<CreateReleaseResponse>('/api/admin/releases', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  listReleases: (query: ListReleasesQuery = {}) =>
    request<ListReleasesResponse>(`/api/admin/releases${qs(query)}`),

  getRelease: (token: string) =>
    request<ReleaseRecord>(`/api/admin/releases/${encodeURIComponent(token)}`),

  resendLink: (token: string) =>
    request<ResendLinkResponse>(`/api/admin/releases/${encodeURIComponent(token)}/resend`, {
      method: 'POST',
    }),

  sendEmail: (token: string, to?: string) =>
    request<SendEmailResponse>(`/api/admin/releases/${encodeURIComponent(token)}/send-email`, {
      method: 'POST',
      body: JSON.stringify({ to }),
    }),

  csvUrl: (query: ListReleasesQuery = {}) => `/api/admin/releases.csv${qs(query)}`,

  refine: (description: string, isCommercial: boolean) =>
    request<{ text: string }>('/api/refine', {
      method: 'POST',
      body: JSON.stringify({ description, isCommercial }),
    }),
};
